import { useCallback, useEffect } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { BoardElsewhere } from "@/features/planning/BoardElsewhere";
import { BoardScreen } from "@/features/planning/BoardScreen";
import { useBoardHold } from "@/features/planning/boardLock";
import { stepOfWord, stepWord, type PlanStep } from "@/features/planning/planSteps";
import { shiftDay, todayIso } from "@/features/trips/tripLabels";
import { applyParamUpdates } from "@/lib/searchParams";
import { recallPlanDay, rememberListSearch } from "@/lib/listState";
import { hasModule, firstAllowedRoute } from "@/lib/auth";

interface BoardSearch {
  /** YYYY-MM-DD, the day planned; without one the plan day chosen before, or tomorrow. */
  date?: string;
  /** A branch code; without one every lorry we have. */
  branch?: string;
  /** The step of Plannen as a word (plan, overview, capacity); without one the first. */
  step?: string;
}

/**
 * The day shown: the day asked for; without one the plan day, chosen here or on Ritten, in this tab or another
 * (lib/listState); without that tomorrow, the static planning.
 */
function firstDayOf(search: BoardSearch): string {
  return search.date ?? recallPlanDay() ?? shiftDay(todayIso(), 1);
}

export const Route = createFileRoute("/_shell/planning")({
  // Reading is tms_planning; planning itself is tms_planning_write.
  beforeLoad: ({ context }) => {
    if (!hasModule(context.session, "tms_planning") && !hasModule(context.session, "tms_planning_write")) {
      throw redirect({ to: firstAllowedRoute(context.session) });
    }
  },
  // A link from before Plannen was the only tab (view, to, trip: the Dagbord, the worksheet, a week) opens Plannen
  // on its first day (27-10-2026).
  validateSearch: (search: Record<string, unknown>): BoardSearch => ({
    date: typeof search.date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(search.date) ? search.date : undefined,
    branch: typeof search.branch === "string" && search.branch ? search.branch : undefined,
    step: stepWord(stepOfWord(search.step)),
  }),
  component: PlanningScreen,
});

function PlanningScreen() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { session } = Route.useRouteContext();
  const canPlan = session.roles.includes("admin") || hasModule(session, "tms_planning_write");
  // One board per browser (P88): in a second tab, *Hier openen* instead of a board that steers the first.
  const { hold, takeOver } = useBoardHold();

  // As you left it, for the Planbord tab: the day, the branch and the step. The day holds until midnight
  // (lib/listState). Before, the tab opened a bare board on today (Mark, 02-10-2026: "Planbord klikken reset de
  // datum"). The day shown, also when the address has none: it is the plan day Ritten opens on (Mark, 23-10-2026).
  const date = firstDayOf(search);
  const { branch, step } = search;
  useEffect(() => {
    rememberListSearch("/planning", { date, branch, step }, { days: ["date"] });
  }, [date, branch, step]);

  const setDate = useCallback(
    (date: string) => {
      navigate({ search: (prev) => applyParamUpdates(prev, { date }), replace: true });
    },
    [navigate],
  );
  const setBranch = useCallback(
    (branch: string) => {
      navigate({ search: (prev) => applyParamUpdates(prev, { branch }), replace: true });
    },
    [navigate],
  );
  const setStep = useCallback(
    (next: PlanStep) => {
      navigate({ search: (prev) => applyParamUpdates(prev, { step: stepWord(next) ?? "" }), replace: true });
    },
    [navigate],
  );

  if (hold === "checking") return null;
  if (hold !== "held") return <BoardElsewhere taken={hold === "taken"} onTakeOver={takeOver} />;
  return (
    <BoardScreen
      date={date}
      branch={branch ?? ""}
      onDate={setDate}
      onBranch={setBranch}
      canPlan={canPlan}
      step={stepOfWord(search.step)}
      onStep={setStep}
    />
  );
}
